import {
  Fee,
  Fuelling,
  Insurance,
  Licensing,
  Maintenance,
  Repair,
  Ticket,
  VehicleRevenue,
} from "@/interfaces";
import { formatDateOnly, formatDateTime, formatToCurrency } from "@/lib/utils";
import { ChevronDown, ChevronUp, Minus, Plus } from "lucide-react";
import React from "react";

interface CategoryData<T> {
  history: T[];
  total: number;
}

interface ExpenseCategories {
  fuelling: CategoryData<Fuelling>;
  licensing: CategoryData<Licensing>;
  maintenance: CategoryData<Maintenance>;
  ticket: CategoryData<Ticket>;
  otherFees: CategoryData<Fee>;
  repair: CategoryData<Repair>;
  insurance: CategoryData<Insurance>;
}

interface VehicleExpenseItemProps {
  vehicle: {
    id: number;
    title: string;
    plate: string;
  };
  total: number;
  revenues: CategoryData<VehicleRevenue>;
  net_result: number;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  categories: ExpenseCategories;
  expandedCategories: string[];
  onToggleCategoryExpansion: (category: string) => void;
  getCategoryLabel: (categoryId: string) => string;
  expenseCategories: {
    id: string;
    label: string;
    fields?: {
      key: string;
      label: string;
      type: string;
    }[];
  }[];
}

const VehicleExpenseItem: React.FC<VehicleExpenseItemProps> = ({
  vehicle,
  total,
  revenues,
  net_result,
  isExpanded,
  onToggleExpansion,
  categories,
  expandedCategories,
  onToggleCategoryExpansion,
  getCategoryLabel,
  expenseCategories,
}) => {
  const getCategoryFields = (categoryId: string) => {
    const category = expenseCategories.find((cat) => cat.id === categoryId);
    return category?.fields || [];
  };

  const getNestedValue = <T, U extends Record<string, unknown>>(
    obj: U,
    path: string,
    fallback: T,
  ): T => {
    if (!obj || typeof obj !== "object") return fallback;
    const keys = path.split(".");
    let result: unknown = obj;

    for (const key of keys) {
      if (
        result === null ||
        result === undefined ||
        typeof result !== "object"
      ) {
        return fallback;
      }
      result = (result as Record<string, unknown>)[key];
    }

    return result as unknown as T;
  };

  const revenueFields = [
    { key: "date", label: "Data", type: "date" },
    { key: "invoice_number", label: "Nº Nota", type: "string" },
    { key: "category.name", label: "Categoria", type: "string" },
    { key: "amount", label: "Valor", type: "currency" },
    { key: "observations", label: "Observações", type: "string" },
  ];

  const revenuesExpanded = expandedCategories.includes("__revenues__");

  return (
    <div className="bg-card shadow-sm rounded-lg p-4 border border-white">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={onToggleExpansion}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <h3 className="text-lg font-medium text-foreground">
            {vehicle.plate} - {vehicle.title}
          </h3>
          <span className="text-sm text-muted-foreground">
            Despesas:{" "}
            <span className="font-medium text-foreground">
              {formatToCurrency(total)}
            </span>
          </span>
          <span className="text-sm text-muted-foreground">
            Receitas:{" "}
            <span className="font-medium text-green-600 dark:text-green-400">
              {formatToCurrency(revenues?.total ?? 0)}
            </span>
          </span>
          <span className="text-sm text-muted-foreground">
            Resultado:{" "}
            <span
              className={`font-medium ${
                net_result >= 0
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {formatToCurrency(net_result)}
            </span>
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-foreground shrink-0" />
        )}
      </div>

      {isExpanded && (
        <div className="mt-4">
          {/* Categorias de despesas */}
          {Object.entries(categories).map(([category, details]) => (
            <div key={category} className="mb-4">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => onToggleCategoryExpansion(category)}
              >
                <div className="flex items-center">
                  <span className="font-medium text-foreground">
                    {getCategoryLabel(category)}
                  </span>
                  <span className="ml-2 text-muted-foreground">{" - "}</span>
                  <span className="ml-2 text-foreground font-medium">
                    {formatToCurrency(details.total)}
                  </span>
                </div>
                <div>
                  {expandedCategories.includes(category) ? (
                    <Minus className="h-4 w-4 text-foreground" />
                  ) : (
                    <Plus className="h-4 w-4 text-foreground" />
                  )}
                </div>
              </div>
              {expandedCategories.includes(category) && (
                <div className="mt-2">
                  {details.history.length > 0 ? (
                    <table className="min-w-full divide-y divide-border">
                      <thead>
                        <tr>
                          {getCategoryFields(category).map((field) => (
                            <th
                              key={field.key}
                              className="px-6 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                            >
                              {field.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-card divide-y divide-border">
                        {details.history.map(
                          (item: Record<string, unknown>, index: number) => (
                            <tr key={index}>
                              {getCategoryFields(category).map(
                                (field: {
                                  key: string;
                                  label: string;
                                  type: string;
                                }) => (
                                  <td
                                    key={field.key}
                                    className="px-6 py-4 whitespace-nowrap text-foreground"
                                  >
                                    {field.type === "date"
                                      ? formatDateOnly(
                                          getNestedValue<string, typeof item>(
                                            item,
                                            field.key,
                                            "",
                                          ),
                                        )
                                      : field.type === "datetime"
                                        ? formatDateTime(
                                            getNestedValue<string, typeof item>(
                                              item,
                                              field.key,
                                              "",
                                            ),
                                          )
                                        : field.type === "currency"
                                          ? formatToCurrency(
                                              getNestedValue<
                                                number,
                                                typeof item
                                              >(item, field.key, 0),
                                            )
                                          : field.type === "number" &&
                                              field.key === "odometer"
                                            ? Math.floor(
                                                getNestedValue<
                                                  number,
                                                  typeof item
                                                >(item, field.key, 0),
                                              ).toLocaleString()
                                            : getNestedValue<
                                                string,
                                                typeof item
                                              >(item, field.key, "")}
                                  </td>
                                ),
                              )}
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted-foreground">
                      Nenhum dado disponível.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Seção de receitas */}
          <div className="mb-4 border-t border-border pt-4">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => onToggleCategoryExpansion("__revenues__")}
            >
              <div className="flex items-center">
                <span className="font-medium text-green-600 dark:text-green-400">
                  Receitas
                </span>
                <span className="ml-2 text-muted-foreground">{" - "}</span>
                <span className="ml-2 font-medium text-green-600 dark:text-green-400">
                  {formatToCurrency(revenues?.total ?? 0)}
                </span>
              </div>
              <div>
                {revenuesExpanded ? (
                  <Minus className="h-4 w-4 text-foreground" />
                ) : (
                  <Plus className="h-4 w-4 text-foreground" />
                )}
              </div>
            </div>

            {revenuesExpanded && (
              <div className="mt-2">
                {(revenues?.history ?? []).length > 0 ? (
                  <table className="min-w-full divide-y divide-border">
                    <thead>
                      <tr>
                        {revenueFields.map((field) => (
                          <th
                            key={field.key}
                            className="px-6 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                          >
                            {field.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {revenues.history.map((item, index) => (
                        <tr key={index}>
                          {revenueFields.map((field) => (
                            <td
                              key={field.key}
                              className="px-6 py-4 whitespace-nowrap text-foreground"
                            >
                              {field.type === "date"
                                ? formatDateOnly(
                                    getNestedValue<
                                      string,
                                      Record<string, unknown>
                                    >(
                                      item as unknown as Record<
                                        string,
                                        unknown
                                      >,
                                      field.key,
                                      "",
                                    ),
                                  )
                                : field.type === "currency"
                                  ? formatToCurrency(
                                      getNestedValue<
                                        number,
                                        Record<string, unknown>
                                      >(
                                        item as unknown as Record<
                                          string,
                                          unknown
                                        >,
                                        field.key,
                                        0,
                                      ),
                                    )
                                  : getNestedValue<
                                      string,
                                      Record<string, unknown>
                                    >(
                                      item as unknown as Record<
                                        string,
                                        unknown
                                      >,
                                      field.key,
                                      "-",
                                    ) || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted-foreground">
                    Nenhuma receita no período.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleExpenseItem;
